import { useState, useEffect, useRef } from "react";
import loadImage from "../../assets/peakScore/load.png";
import wellnessImage from "../../assets/peakScore/wellness.png";
import physiologyImage from "../../assets/peakScore/physiology.png";
import loadOverlay from "../../assets/peakScore/LoadOverlay.png";
import wellnessOverlay from "../../assets/peakScore/WellnessOverlay.png";
import physiologyOverlay from "../../assets/peakScore/PhysiologyOverlay.png";

interface PeakCardProps {
  title: string;
  summary: string;
  detail: string;
  bgColor: string;
  imageUrl?: string;
  overlayUrl?: string;
}

const getPeakScoreColor = (score: number): string => {
  if (score >= 100) return "#DAA520";
  if (score >= 80) return "#0096c7";
  if (score >= 60) return "#22c55e";
  if (score >= 30) return "#f97316";
  return "#ef4444";
};

const AnimatedPeakScore = () => {
  const [animatedScore, setAnimatedScore] = useState(60);
  const [hasAnimated, setHasAnimated] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const startScore = 60;
  const targetScore = 100;
  const baseScore = 110;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            const duration = 2000;
            const steps = 60;
            const scoreRange = targetScore - startScore;
            const increment = scoreRange / steps;
            let currentStep = 0;

            const timer = setInterval(() => {
              currentStep++;
              if (currentStep <= steps) {
                setAnimatedScore(
                  Math.min(startScore + currentStep * increment, targetScore),
                );
              } else {
                clearInterval(timer);
              }
            }, duration / steps);
          }
        });
      },
      { threshold: 0.3 },
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [hasAnimated]);

  const peakPercentage = Math.min((animatedScore / baseScore) * 100, 100);
  const peakColor = getPeakScoreColor(animatedScore);
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(peakPercentage / 100) * circumference} ${circumference}`;

  return (
    <div
      ref={componentRef}
      className="relative inline-flex items-center justify-center bg-base-100 rounded-full shadow-md"
      style={{ width: "12rem", height: "12rem" }}
    >
      <svg
        className="transform -rotate-90"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="6"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={peakColor}
          strokeWidth="6"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          className="transition-all duration-300 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-bold text-7xl text-base-content font-heading"
          aria-label={`Peak score: ${Math.round(animatedScore)}`}
        >
          {Math.round(animatedScore)}
        </span>
      </div>
    </div>
  );
};

const PeakCard = ({
  title,
  summary,
  detail,
  bgColor,
  imageUrl,
  overlayUrl,
}: PeakCardProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative flex-1 rounded-2xl overflow-hidden cursor-pointer"
      style={{ minHeight: "480px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out"
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
          backgroundColor: imageUrl ? undefined : bgColor,
          transform: hovered ? "scale(1.04)" : "scale(1)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: imageUrl
            ? `linear-gradient(to top, ${bgColor}EE, ${bgColor}BB, ${bgColor}77, transparent)`
            : undefined,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end p-8">
        {overlayUrl && (
          <img
            src={overlayUrl}
            alt={`${title} data visualization`}
            className="w-100 object-contain"
          />
        )}
        <span className="text-xs font-bold uppercase tracking-wider text-white/50 font-grotesk mb-1">
          Factor
        </span>
        <h3 className="text-white font-bold text-3xl mb-2 font-heading">
          {title}
        </h3>
        <p className="text-white/90 text-base leading-tight font-grotesk mb-2">
          {summary}
        </p>
        <p className="text-white/60 text-xs leading-relaxed font-grotesk">
          {detail}
        </p>
      </div>
    </div>
  );
};

const peakFactors = [
  {
    title: "Load",
    summary: "What training you've been exposed to vs. what you're used to.",
    detail:
      "Compares your recent acute training load against your chronic baseline to detect spikes or drops.",
    bgColor: "#374151",
    imageUrl: loadImage,
    overlayUrl: loadOverlay,
  },
  {
    title: "Wellness",
    summary: "How you feel and respond to training daily.",
    detail:
      "Subjective wellness check-ins capture how your body is experiencing training stress.",
    bgColor: "#15803d",
    imageUrl: wellnessImage,
    overlayUrl: wellnessOverlay,
  },
  {
    title: "Physiology",
    summary: "How your physiology is responding over time.",
    detail:
      "Pulled from wearable data (HRV, RHR, respiratory rate) to measure cardiorespiratory adaptation.",
    bgColor: "#6b21a8",
    imageUrl: physiologyImage,
    overlayUrl: physiologyOverlay,
  },
];

export default function PeakScoreSection() {
  return (
    <div className="bg-base-200 py-24 flex flex-col items-center px-6">
      <div className="flex flex-col max-w-5xl mx-auto gap-16 md:gap-24 w-full">
        {/* Hero: Score + Intro */}
        <div className="flex flex-col md:flex-row gap-10 md:gap-12 items-center w-full">
          <div className="flex justify-center md:flex-1 order-1 md:order-2">
            <AnimatedPeakScore />
          </div>

          <div className="flex flex-col gap-5 justify-center md:flex-[2] order-2 md:order-1">
            <div>
              <p className="text-base text-left font-grotesk text-base-content/60 font-medium mb-1">
                The science behind the
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-grotesk text-left leading-tight">
                Peak <span className="font-light">Readiness Score</span>
              </h2>
            </div>

            <p className="text-lg text-left text-base-content/80 font-grotesk leading-relaxed">
              A daily readiness signal designed for athletes to make better
              training decisions. It reflects how prepared you are to tolerate
              training stress and express performance on a given day.
            </p>

            <p className="text-base text-left font-grotesk leading-relaxed font-medium text-base-content">
              The Peak Score captures what wearables cannot — the context. What
              the athlete is actually doing and how they feel.
            </p>

            <p className="text-sm text-left text-base-content/60 font-grotesk leading-relaxed">
              The score is relative to you — not a prediction of performance or
              a comparison to other athletes. It tells you if today's training
              demands are aligned with your current state.
            </p>

            <div>
              <a
                href="https://learn.peak.app/what-is-the-peak-score/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-base-300 text-base-content/70 py-2.5 px-6 rounded-full font-grotesk text-sm hover:bg-base-300 transition-colors font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Learn more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* How it's calculated — Combined Factor Cards */}
        <div>
          <h3 className="text-4xl md:text-5xl font-bold font-grotesk text-left mb-3">
            How it's calculated
          </h3>
          <p className="text-lg md:text-xl text-left text-base-content/70 font-grotesk leading-relaxed mb-8 max-w-2xl">
            The Peak Score is based on three realities that matter most for
            readiness.
          </p>

          <div className="flex flex-col md:flex-row gap-4 w-full">
            {peakFactors.map((factor) => (
              <PeakCard
                key={factor.title}
                title={factor.title}
                summary={factor.summary}
                detail={factor.detail}
                bgColor={factor.bgColor}
                imageUrl={factor.imageUrl}
                overlayUrl={factor.overlayUrl}
              />
            ))}
          </div>

          <p className="text-xs text-base-content/50 font-grotesk mt-4">
            * The Physiology factor uses wearable data when available but is not
            required. It enhances the depth of insights provided.
          </p>
        </div>

        {/* Expert Quote */}
        <figure className="pb-16 mt-4 md:mt-8">
          <blockquote className="flex flex-col gap-6 relative pl-6 border-l-4 border-base-300">
            <p className="text-xl sm:text-2xl md:text-3xl font-medium font-grotesk text-base-content/80 leading-snug">
              The Peak score will give you the full picture of readiness that
              wearables miss. You need to combine{" "}
              <strong className="text-base-content">
                training load, subjective wellness, and physiology from wearable
                data
              </strong>
              , to confidently see what's driving your readiness today. Wearable
              devices alone are not capable of this.
            </p>
          </blockquote>
          <figcaption className="mt-6 pl-6">
            <p className="text-lg font-semibold font-grotesk">Sam Leslie</p>
            <p className="text-sm text-base-content/60 font-grotesk">
              Creator of the Peak Score & Chief Medical Officer
            </p>
            <p className="text-xs text-base-content/40 font-grotesk mt-1">
              30 years experience as Sports Physiotherapist, Olympic Coach &
              Researcher
            </p>
          </figcaption>
        </figure>
      </div>
    </div>
  );
}
